import type {
  Resource,
  AvailabilityRule,
  AvailabilityException,
  Booking,
  TimeSlot,
} from "../schema/booking.js";

function toMinutes(time: string): number {
  const parts = time.split(":");
  return Number(parts[0]) * 60 + Number(parts[1]);
}

function fromMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function getDayOfWeek(dateStr: string): number {
  // Parse YYYY-MM-DD without timezone issues
  const parts = dateStr.split("-").map(Number);
  return new Date(parts[0]!, parts[1]! - 1, parts[2]).getDay();
}

export interface GenerateSlotsOptions {
  resource: Resource;
  rules: AvailabilityRule[];
  exceptions: AvailabilityException[];
  existingBookings: Booking[];
  date: string;          // "YYYY-MM-DD"
  granularityMinutes: number; // e.g. 15, 30, 60
  durationMinutes: number;    // length of the requested booking
  partySize?: number;
}

export function generateSlots(opts: GenerateSlotsOptions): TimeSlot[] {
  const {
    resource,
    rules,
    exceptions,
    existingBookings,
    date,
    granularityMinutes,
    durationMinutes,
    partySize = 1,
  } = opts;

  if (!resource.active) return [];

  const exception = exceptions.find((e) => e.date === date);
  if (exception?.closed) return [];

  const dayOfWeek = getDayOfWeek(date);

  // Determine open window for the day
  let windowStart: number | null = null;
  let windowEnd: number | null = null;

  if (exception && !exception.closed && exception.startTime && exception.endTime) {
    windowStart = toMinutes(exception.startTime);
    windowEnd = toMinutes(exception.endTime);
  } else {
    const rule = rules
      .filter((r) => r.resourceId === resource.id && r.days.includes(dayOfWeek))
      // If multiple rules match the same day, use the one with the widest window
      .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))[0];
    if (!rule) return [];
    windowStart = toMinutes(rule.startTime);
    windowEnd = toMinutes(rule.endTime);
  }

  if (windowStart === null || windowEnd === null) return [];

  const dayBookings = existingBookings.filter(
    (b) =>
      b.resourceId === resource.id &&
      b.date === date &&
      b.status !== "cancelled"
  );

  const slots: TimeSlot[] = [];

  for (
    let start = windowStart;
    start + durationMinutes <= windowEnd;
    start += granularityMinutes
  ) {
    const end = start + durationMinutes;
    const startStr = fromMinutes(start);
    const endStr = fromMinutes(end);

    // Count overlapping bookings
    const overlapping = dayBookings.filter((b) => {
      const bStart = toMinutes(b.startTime);
      const bEnd = toMinutes(b.endTime);
      return bStart < end && bEnd > start;
    });

    // For party-size-aware resources (tables), check if partySize fits
    const capacityFits =
      partySize >= resource.capacityMin && partySize <= resource.capacityMax;

    // Slot is available if no conflicting bookings AND party size fits
    const available = overlapping.length === 0 && capacityFits;
    const remainingCapacity = available ? resource.capacityMax - (partySize - 1) : 0;

    slots.push({
      resourceId: resource.id,
      date,
      startTime: startStr,
      endTime: endStr,
      available,
      remainingCapacity,
    });
  }

  return slots;
}
